import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CalendarClock,
  Check,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Pencil,
  Plus,
  SendHorizonal,
  X,
} from "lucide-react";
import {
  api,
  getAssetBlobUrl,
  type Brand,
  type Campaign,
  type Post,
} from "../../lib/api";
import PostPreview from "../../components/PostPreview";

// ─── Platform brand icons (inline SVG — no trademark dep needed) ──────────────

function IconLinkedIn({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function IconX({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
    </svg>
  );
}

function IconInstagram({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );
}

function IconFacebook({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────

interface PlatformMeta {
  icon: React.FC<{ size?: number }>;
  color: string;
  short: string;
  label: string;
}

const PLATFORM_META: Record<string, PlatformMeta> = {
  twitter: { icon: IconX, color: "#000000", short: "X", label: "X / Twitter" },
  linkedin: {
    icon: IconLinkedIn,
    color: "#0A66C2",
    short: "LinkedIn",
    label: "LinkedIn",
  },
  instagram_feed: {
    icon: IconInstagram,
    color: "#E1306C",
    short: "Instagram",
    label: "Instagram Feed",
  },
  instagram_story: {
    icon: IconInstagram,
    color: "#833AB4",
    short: "Story",
    label: "Instagram Story",
  },
  facebook: {
    icon: IconFacebook,
    color: "#1877F2",
    short: "Facebook",
    label: "Facebook",
  },
};

const ALL_PLATFORMS = Object.keys(PLATFORM_META);

const PLATFORM_CHARS: Record<string, number> = {
  twitter: 280,
  instagram_feed: 2200,
  instagram_story: 80,
  linkedin: 3000,
  facebook: 500,
};

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "pending_review", label: "Pending" },
  { value: "needs_revision", label: "Needs Revision" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "scheduled", label: "Scheduled" },
];

// ─── Engagement score heuristic ───────────────────────────────────────────────
// Scores post copy 1.0–5.0 based on length, emoji, CTA words, and hashtag count.

const OPTIMAL_LENGTH: Record<string, [number, number]> = {
  twitter: [80, 180],
  instagram_feed: [100, 300],
  instagram_story: [30, 80],
  linkedin: [100, 400],
  facebook: [60, 200],
  bluesky: [80, 200],
};

function computeEngagementScore(
  copy: string,
  platform: string,
  hashtags: string[],
): number {
  let score = 2.5;
  const len = copy.length;
  const [lo, hi] = OPTIMAL_LENGTH[platform] ?? [80, 200];

  // Length bonus/penalty
  if (len >= lo && len <= hi) score += 1.0;
  else if (len > 0 && len < lo) score += 0.3;
  else if (len > hi) score -= 0.3;
  if (len < 20) score -= 1.2;

  // Emoji presence (broad check — works for most common emoji ranges)
  if (
    /[\u{1F300}-\u{1FAFF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u.test(copy)
  )
    score += 0.5;

  // Engagement punctuation
  if (/[?!]/.test(copy)) score += 0.3;

  // CTA words
  if (
    /\b(buy|shop|click|link|sign up|learn more|discover|get|join|try|start|explore|visit)\b/i.test(
      copy,
    )
  )
    score += 0.4;

  // Hashtag count (2–5 is optimal)
  const hc = hashtags.length;
  if (hc >= 2 && hc <= 5) score += 0.3;
  else if (hc > 10) score -= 0.3;

  return Math.min(5.0, Math.max(1.0, Math.round(score * 10) / 10));
}

function EngagementBadge({ score }: { score: number }) {
  const color =
    score >= 4.0
      ? "text-green-600 bg-green-50"
      : score >= 2.5
        ? "text-amber-600 bg-amber-50"
        : "text-red-500 bg-red-50";
  const filled = Math.round(score);
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded ${color}`}
      title={`Estimated engagement: ${score}/5`}
    >
      {"★".repeat(filled)}
      {"☆".repeat(5 - filled)}
      <span className="ml-0.5 opacity-70">{score}</span>
    </span>
  );
}

// ─── Skeleton card ─────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="card px-5 py-4 animate-pulse">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="h-3 w-16 bg-gray-200 rounded-full" />
        <div className="h-3 w-10 bg-gray-100 rounded-full" />
      </div>
      <div className="h-3 w-3/4 bg-gray-200 rounded-full mb-2" />
      <div className="h-3 w-full bg-gray-100 rounded-full mb-2" />
      <div className="h-3 w-2/3 bg-gray-100 rounded-full" />
    </div>
  );
}

const ACTIVE_JOBS_KEY = "md_active_jobs";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ActiveJob {
  jobId: string;
  campaignId: string;
  campaignName: string;
  brandId: string;
}

type JobStatus = "queued" | "processing" | "completed" | "failed";

interface PostGroup {
  groupId: string;
  posts: Post[];
}

type ListItem =
  | { kind: "standalone"; post: Post }
  | { kind: "group"; group: PostGroup };

type Selection =
  | { kind: "standalone"; post: Post }
  | { kind: "thread"; group: PostGroup; activeIndex: number };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function readActiveJobs(): ActiveJob[] {
  try {
    return JSON.parse(localStorage.getItem(ACTIVE_JOBS_KEY) ?? "[]");
  } catch {
    return [];
  }
}

function writeActiveJobs(jobs: ActiveJob[]): void {
  localStorage.setItem(ACTIVE_JOBS_KEY, JSON.stringify(jobs));
}

function buildListItems(posts: Post[]): ListItem[] {
  const groups: Record<string, Post[]> = {};
  const seen = new Set<string>();

  for (const post of posts) {
    if (post.groupId) {
      if (!groups[post.groupId]) groups[post.groupId] = [];
      groups[post.groupId].push(post);
    }
  }

  const result: ListItem[] = [];
  for (const post of posts) {
    if (!post.groupId) {
      result.push({ kind: "standalone", post });
    } else if (!seen.has(post.groupId)) {
      seen.add(post.groupId);
      const sorted = (groups[post.groupId] ?? []).sort(
        (a, b) => (a.threadOrder ?? 0) - (b.threadOrder ?? 0),
      );
      result.push({
        kind: "group",
        group: { groupId: post.groupId, posts: sorted },
      });
    }
  }

  return result;
}

function formatRelative(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function PlatformIndicator({
  platform,
  size = 14,
}: {
  platform: string;
  size?: number;
}) {
  const meta = PLATFORM_META[platform];
  if (!meta) return <span className="text-xs text-gray-400">{platform}</span>;
  const Icon = meta.icon;
  return (
    <span
      className="flex items-center gap-1.5 flex-shrink-0"
      style={{ color: meta.color }}
    >
      <Icon size={size} />
      <span className="text-xs font-medium" style={{ color: meta.color }}>
        {meta.short}
      </span>
    </span>
  );
}

// ─── Bulk action bar ──────────────────────────────────────────────────────────

function BulkActionBar({
  count,
  onApprove,
  onReject,
  onSchedule,
  onClear,
  acting,
}: {
  count: number;
  onApprove: () => void;
  onReject: () => void;
  onSchedule: (iso: string) => void;
  onClear: () => void;
  acting: boolean;
}) {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [bulkDate, setBulkDate] = useState("");

  return (
    <div className="flex-shrink-0 mb-3 flex items-center gap-3 bg-gray-900 text-white rounded-xl px-4 py-3">
      <span className="text-sm font-semibold flex-1">
        {count} post{count !== 1 ? "s" : ""} selected
      </span>
      <button
        onClick={onApprove}
        disabled={acting}
        className="text-xs bg-green-500 hover:bg-green-600 px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50"
      >
        Approve all
      </button>
      <button
        onClick={() => setShowDatePicker((v) => !v)}
        disabled={acting}
        className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 ${showDatePicker ? "bg-brand-500" : "bg-gray-700 hover:bg-gray-600"}`}
      >
        Schedule all
      </button>
      <button
        onClick={onReject}
        disabled={acting}
        className="text-xs bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50"
      >
        Reject all
      </button>
      <button
        onClick={onClear}
        className="text-xs text-gray-400 hover:text-white transition-colors"
        aria-label="Clear selection"
      >
        ✕ Clear
      </button>
      {showDatePicker && (
        <div className="absolute z-10 mt-12 bg-white border border-gray-200 rounded-xl shadow-xl p-3 flex gap-2">
          <input
            type="datetime-local"
            className="input text-sm"
            min={new Date().toISOString().slice(0, 16)}
            value={bulkDate}
            onChange={(e) => setBulkDate(e.target.value)}
          />
          <button
            onClick={() => {
              if (bulkDate) {
                onSchedule(bulkDate);
                setShowDatePicker(false);
              }
            }}
            disabled={!bulkDate || acting}
            className="btn-primary text-sm disabled:opacity-50"
          >
            Confirm
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ReviewQueue() {
  // URL search params (supports deep-linking from campaigns page)
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter state
  const [platform, setPlatform] = useState("");
  const [status, setStatus] = useState("pending_review");
  const [filterBrandId, setFilterBrandId] = useState(
    searchParams.get("brandId") ?? "",
  );
  const [filterCampaignId, setFilterCampaignId] = useState(
    searchParams.get("campaignId") ?? "",
  );

  // Brand + Campaign options for filter dropdowns
  const [brands, setBrands] = useState<Brand[]>([]);
  const [campaignOptions, setCampaignOptions] = useState<Campaign[]>([]);

  // List state
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  // Selection (right panel)
  const [selection, setSelection] = useState<Selection | null>(null);

  // Edit state (keyed by postId)
  const [editCopy, setEditCopy] = useState<Record<string, string>>({});
  const [editHashtags, setEditHashtags] = useState<Record<string, string[]>>(
    {},
  );
  const [rejectNote, setRejectNote] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);

  // Action card UI state (keyed by postId or groupId)
  const [showRejectNote, setShowRejectNote] = useState<Record<string, boolean>>(
    {},
  );
  const [showSchedulePicker, setShowSchedulePicker] = useState<
    Record<string, boolean>
  >({});
  const [scheduleDate, setScheduleDate] = useState<Record<string, string>>({});

  // Hashtag add-tag inline input
  const [addingTagFor, setAddingTagFor] = useState<string | null>(null);
  const [newTagInput, setNewTagInput] = useState("");

  // Active generation banners
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);
  const [jobStatuses, setJobStatuses] = useState<Record<string, JobStatus>>({});
  const jobIntervals = useRef<Record<string, ReturnType<typeof setInterval>>>(
    {},
  );

  // Per-post regeneration tracking (postId → true while dispatching)
  const [regeneratingPostId, setRegeneratingPostId] = useState<string | null>(
    null,
  );

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkActing, setBulkActing] = useState(false);

  // "Needs Revision" note (keyed by postId)
  const [revisionNote, setRevisionNote] = useState<Record<string, string>>({});
  const [showRevisionNote, setShowRevisionNote] = useState<
    Record<string, boolean>
  >({});

  // Caches
  const campaignCache = useRef<Record<string, Campaign>>({});
  const brandCache = useRef<Record<string, Brand>>({});
  const brandNameMap = useRef<Record<string, string>>({});

  // Image management
  const [campaignAssets, setCampaignAssets] = useState<
    Record<string, string[]>
  >({});
  const [assetBlobUrls, setAssetBlobUrls] = useState<Record<string, string>>(
    {},
  );
  const [savingImage, setSavingImage] = useState<string | null>(null);
  const uploadInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Preview image for right panel
  const [previewImageUrl, setPreviewImageUrl] = useState<string | undefined>();

  // Force re-render when brandNameMap is populated
  const [, setBrandNamesLoaded] = useState(0);

  // ── Derived ───────────────────────────────────────────────────────────────────

  const listItems = buildListItems(posts);

  const selectedPost: Post | null =
    selection === null
      ? null
      : selection.kind === "standalone"
        ? selection.post
        : (selection.group.posts[selection.activeIndex] ?? null);

  // ── Data loading ──────────────────────────────────────────────────────────────

  const load = useCallback(() => {
    setLoading(true);
    api.posts
      .list({
        status: status || undefined,
        platform: platform || undefined,
        brandId: filterBrandId || undefined,
        campaignId: filterCampaignId || undefined,
      })
      .then((r) => setPosts(r.posts))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [status, platform, filterBrandId, filterCampaignId]);

  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    load();
  }, [load]);

  // Load brand names once on mount for list card display + filter dropdown
  useEffect(() => {
    api.brands
      .list()
      .then(({ brands: allBrands }) => {
        allBrands.forEach((b) => {
          brandNameMap.current[b.brandId] = b.name;
        });
        setBrands(allBrands);
        setBrandNamesLoaded((n) => n + 1);

        // If we arrived with a campaignId but no brandId, resolve the brand
        // from the campaign's posts so the brand dropdown is pre-selected.
        if (!filterBrandId && filterCampaignId && allBrands.length > 0) {
          // Load campaigns for every brand to find the matching one
          Promise.all(
            allBrands.map((b) =>
              api.campaigns
                .list(b.brandId)
                .then(({ campaigns }) => ({ brandId: b.brandId, campaigns })),
            ),
          ).then((results) => {
            for (const { brandId: bId, campaigns } of results) {
              if (campaigns.some((c) => c.campaignId === filterCampaignId)) {
                setFilterBrandId(bId);
                setCampaignOptions(campaigns);
                return;
              }
            }
          });
        }
      })
      .catch(() => {
        /* non-fatal */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load campaign options when brand filter changes
  useEffect(() => {
    if (!filterBrandId) {
      setCampaignOptions([]);
      return;
    }
    api.campaigns
      .list(filterBrandId)
      .then(({ campaigns }) => setCampaignOptions(campaigns))
      .catch(() => setCampaignOptions([]));
  }, [filterBrandId]);

  // Keep URL search params in sync with filter state
  useEffect(() => {
    const params = new URLSearchParams();
    if (filterBrandId) params.set("brandId", filterBrandId);
    if (filterCampaignId) params.set("campaignId", filterCampaignId);
    setSearchParams(params, { replace: true });
  }, [filterBrandId, filterCampaignId, setSearchParams]);

  // ── Generation job polling ─────────────────────────────────────────────────────

  const startJobPolling = useCallback((job: ActiveJob) => {
    if (jobIntervals.current[job.jobId]) return;
    setJobStatuses((prev) => ({ ...prev, [job.jobId]: "queued" }));

    const interval = setInterval(async () => {
      try {
        const result = await api.generate.status(job.jobId);
        const s = result.status as JobStatus;
        setJobStatuses((prev) => ({ ...prev, [job.jobId]: s }));

        if (s === "completed" || s === "failed") {
          clearInterval(jobIntervals.current[job.jobId]);
          delete jobIntervals.current[job.jobId];
          const remaining = readActiveJobs().filter(
            (j) => j.jobId !== job.jobId,
          );
          writeActiveJobs(remaining);
          if (s === "completed") loadRef.current();
        }
      } catch {
        /* ignore poll errors */
      }
    }, 3000);

    jobIntervals.current[job.jobId] = interval;
  }, []);

  useEffect(() => {
    const jobs = readActiveJobs();
    if (!jobs.length) return;
    setActiveJobs(jobs);
    const intervals = jobIntervals.current;
    for (const job of jobs) startJobPolling(job);
    return () => {
      for (const id of Object.keys(intervals)) {
        clearInterval(intervals[id]);
      }
    };
  }, [startJobPolling]);

  const dismissJob = (jobId: string) => {
    clearInterval(jobIntervals.current[jobId]);
    delete jobIntervals.current[jobId];
    const remaining = readActiveJobs().filter((j) => j.jobId !== jobId);
    writeActiveJobs(remaining);
    setActiveJobs((prev) => prev.filter((j) => j.jobId !== jobId));
    setJobStatuses((prev) => {
      const n = { ...prev };
      delete n[jobId];
      return n;
    });
  };

  // ── Campaign assets ────────────────────────────────────────────────────────────

  const loadCampaignAssets = async (post: Post) => {
    const { brandId, campaignId } = post;
    const cacheKey = `${brandId}:${campaignId}`;

    if (campaignCache.current[cacheKey]) {
      setCampaignAssets((prev) => ({
        ...prev,
        [campaignId]: [...new Set(campaignCache.current[cacheKey].assetR2Keys)],
      }));
      return;
    }

    try {
      const { campaign } = await api.campaigns.get(brandId, campaignId);
      campaignCache.current[cacheKey] = campaign;
      setCampaignAssets((prev) => ({
        ...prev,
        [campaignId]: [...new Set(campaign.assetR2Keys)],
      }));

      for (const r2Key of campaign.assetR2Keys) {
        getAssetBlobUrl(r2Key)
          .then((url) =>
            setAssetBlobUrls((prev) => ({ ...prev, [r2Key]: url })),
          )
          .catch(() => {
            /* skip */
          });
      }
    } catch {
      /* silently skip */
    }
  };

  // Sync editHashtags when selected post changes (only seed if not already edited)
  useEffect(() => {
    if (!selectedPost) return;
    setEditHashtags((prev) => {
      if (prev[selectedPost.postId] !== undefined) return prev;
      return { ...prev, [selectedPost.postId]: [...selectedPost.hashtags] };
    });
  }, [selectedPost]);

  // ── Preview image sync ─────────────────────────────────────────────────────────

  useEffect(() => {
    setPreviewImageUrl(undefined);
    if (!selectedPost?.imageR2Key) return;
    if (assetBlobUrls[selectedPost.imageR2Key]) {
      setPreviewImageUrl(assetBlobUrls[selectedPost.imageR2Key]);
      return;
    }
    getAssetBlobUrl(selectedPost.imageR2Key)
      .then(setPreviewImageUrl)
      .catch(() => {
        /* no image */
      });
  }, [selectedPost, assetBlobUrls]);

  // Brand colors for preview
  useEffect(() => {
    if (!selectedPost) return;
    const { brandId } = selectedPost;
    if (brandCache.current[brandId]) return;
    api.brands
      .get(brandId)
      .then(({ brand }) => {
        brandCache.current[brandId] = brand;
      })
      .catch(() => {
        /* skip */
      });
  }, [selectedPost]);

  // ── Selection handlers ─────────────────────────────────────────────────────────

  const handleSelectStandalone = (post: Post) => {
    setSelection({ kind: "standalone", post });
    loadCampaignAssets(post);
  };

  const handleSelectGroup = (group: PostGroup) => {
    setSelection({ kind: "thread", group, activeIndex: 0 });
    if (group.posts[0]) loadCampaignAssets(group.posts[0]);
  };

  const handleThreadNav = (delta: number) => {
    if (selection?.kind !== "thread") return;
    const next = Math.max(
      0,
      Math.min(selection.group.posts.length - 1, selection.activeIndex + delta),
    );
    setSelection({ ...selection, activeIndex: next });
  };

  // ── Image handlers ─────────────────────────────────────────────────────────────

  const handleSelectImage = async (post: Post, r2Key: string) => {
    const newKey = post.imageR2Key === r2Key ? null : r2Key;
    setSavingImage(post.postId);
    try {
      await api.posts.update(post.postId, {
        imageR2Key: newKey,
      } as Partial<Post>);
      setPosts((prev) =>
        prev.map((p) =>
          p.postId === post.postId ? { ...p, imageR2Key: newKey } : p,
        ),
      );
      if (
        selection?.kind === "standalone" &&
        selection.post.postId === post.postId
      ) {
        setSelection({
          kind: "standalone",
          post: { ...selection.post, imageR2Key: newKey },
        });
      }
      if (selection?.kind === "thread") {
        const updatedPosts = selection.group.posts.map((p) =>
          p.postId === post.postId ? { ...p, imageR2Key: newKey } : p,
        );
        setSelection({
          ...selection,
          group: { ...selection.group, posts: updatedPosts },
        });
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to update image");
    } finally {
      setSavingImage(null);
    }
  };

  const handleUploadImage = async (post: Post, file: File) => {
    setSavingImage(post.postId);
    try {
      const { r2Key } = await api.campaigns.uploadImage(
        post.brandId,
        post.campaignId,
        file,
      );
      delete campaignCache.current[`${post.brandId}:${post.campaignId}`];
      await loadCampaignAssets(post);
      await api.posts.update(post.postId, {
        imageR2Key: r2Key,
      } as Partial<Post>);
      setPosts((prev) =>
        prev.map((p) =>
          p.postId === post.postId ? { ...p, imageR2Key: r2Key } : p,
        ),
      );
    } catch (e) {
      alert(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setSavingImage(null);
    }
  };

  // ── Approve / Reject ───────────────────────────────────────────────────────────

  const handleApprove = async (postId: string) => {
    setSaving(postId);
    const updates: Partial<import("../../lib/api").Post> = {};
    const copy = editCopy[postId];
    if (copy !== undefined) updates.copy = copy;
    const tags = editHashtags[postId];
    if (tags !== undefined) updates.hashtags = tags;
    if (Object.keys(updates).length > 0)
      await api.posts.update(postId, updates);
    await api.posts.approve(postId);
    setPosts((prev) => prev.filter((p) => p.postId !== postId));
    if (selection?.kind === "standalone" && selection.post.postId === postId)
      setSelection(null);
    setSaving(null);
  };

  const handleReject = async (postId: string) => {
    setSaving(postId);
    await api.posts.reject(postId, rejectNote[postId] ?? "");
    setPosts((prev) => prev.filter((p) => p.postId !== postId));
    if (selection?.kind === "standalone" && selection.post.postId === postId)
      setSelection(null);
    setSaving(null);
  };

  const handleSchedule = async (postId: string) => {
    const iso = scheduleDate[postId];
    if (!iso) return;
    setSaving(postId);
    const updates: Partial<import("../../lib/api").Post> = {};
    const copy = editCopy[postId];
    if (copy !== undefined) updates.copy = copy;
    const tags = editHashtags[postId];
    if (tags !== undefined) updates.hashtags = tags;
    if (Object.keys(updates).length > 0)
      await api.posts.update(postId, updates);
    await api.posts.schedule(postId, new Date(iso).getTime());
    setPosts((prev) => prev.filter((p) => p.postId !== postId));
    if (selection?.kind === "standalone" && selection.post.postId === postId)
      setSelection(null);
    setSaving(null);
  };

  const handleApproveGroup = async (group: PostGroup) => {
    setSaving(group.groupId);
    await Promise.all(
      group.posts.map(async (p) => {
        const updates: Partial<import("../../lib/api").Post> = {};
        const copy = editCopy[p.postId];
        if (copy !== undefined) updates.copy = copy;
        const tags = editHashtags[p.postId];
        if (tags !== undefined) updates.hashtags = tags;
        if (Object.keys(updates).length > 0)
          await api.posts.update(p.postId, updates);
      }),
    );
    await api.posts.approveGroup(group.groupId);
    const postIds = new Set(group.posts.map((p) => p.postId));
    setPosts((prev) => prev.filter((p) => !postIds.has(p.postId)));
    setSelection(null);
    setSaving(null);
  };

  const handleRejectGroup = async (group: PostGroup) => {
    setSaving(group.groupId);
    await api.posts.rejectGroup(group.groupId, rejectNote[group.groupId] ?? "");
    const postIds = new Set(group.posts.map((p) => p.postId));
    setPosts((prev) => prev.filter((p) => !postIds.has(p.postId)));
    setSelection(null);
    setSaving(null);
  };

  const handleScheduleGroup = async (group: PostGroup) => {
    const iso = scheduleDate[group.groupId];
    if (!iso) return;
    setSaving(group.groupId);
    await Promise.all(
      group.posts.map(async (p) => {
        const updates: Partial<import("../../lib/api").Post> = {};
        const copy = editCopy[p.postId];
        if (copy !== undefined) updates.copy = copy;
        const tags = editHashtags[p.postId];
        if (tags !== undefined) updates.hashtags = tags;
        if (Object.keys(updates).length > 0)
          await api.posts.update(p.postId, updates);
      }),
    );
    await api.posts.scheduleGroup(group.groupId, new Date(iso).getTime());
    const postIds = new Set(group.posts.map((p) => p.postId));
    setPosts((prev) => prev.filter((p) => !postIds.has(p.postId)));
    setSelection(null);
    setSaving(null);
  };

  // ── Revert to pending (approved → pending_review, in-place, no list refresh) ─

  const handleRevertToPending = async (postId: string) => {
    setSaving(postId);
    try {
      const { post: updated } = await api.posts.revertToPending(postId);
      setPosts((prev) => prev.map((p) => (p.postId === postId ? updated : p)));
      if (
        selection?.kind === "standalone" &&
        selection.post.postId === postId
      ) {
        setSelection({ kind: "standalone", post: updated });
      }
      if (selection?.kind === "thread") {
        const updatedPosts = selection.group.posts.map((p) =>
          p.postId === postId ? updated : p,
        );
        setSelection({
          ...selection,
          group: { ...selection.group, posts: updatedPosts },
        });
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to revert post");
    } finally {
      setSaving(null);
    }
  };

  const handleRevertGroupToPending = async (group: PostGroup) => {
    setSaving(group.groupId);
    try {
      const updatedPosts = await Promise.all(
        group.posts.map(async (p) => {
          const { post: updated } = await api.posts.revertToPending(p.postId);
          return updated;
        }),
      );
      setPosts((prev) =>
        prev.map((p) => {
          const updated = updatedPosts.find((u) => u.postId === p.postId);
          return updated ?? p;
        }),
      );
      if (
        selection?.kind === "thread" &&
        selection.group.groupId === group.groupId
      ) {
        setSelection({
          ...selection,
          group: { ...selection.group, posts: updatedPosts },
        });
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to revert thread");
    } finally {
      setSaving(null);
    }
  };

  // ── Send for revision ─────────────────────────────────────────────────────────

  const handleSendForRevision = async (postId: string) => {
    setSaving(postId);
    try {
      const { post: updated } = await api.posts.sendForRevision(
        postId,
        revisionNote[postId] ?? "",
      );
      setPosts((prev) => prev.map((p) => (p.postId === postId ? updated : p)));
      if (
        selection?.kind === "standalone" &&
        selection.post.postId === postId
      ) {
        setSelection({ kind: "standalone", post: updated });
      }
      // Clear panel if we're viewing "pending_review" filter and this post moved out
      if (status === "pending_review") {
        setPosts((prev) => prev.filter((p) => p.postId !== postId));
        setSelection(null);
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to mark for revision");
    } finally {
      setSaving(null);
    }
  };

  const toggleRevisionNote = (key: string) => {
    setShowRevisionNote((prev) => ({ ...prev, [key]: !prev[key] }));
    setShowRejectNote((prev) => ({ ...prev, [key]: false }));
    setShowSchedulePicker((prev) => ({ ...prev, [key]: false }));
  };

  // ── Bulk operations ────────────────────────────────────────────────────────────

  const toggleSelectId = (postId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkApprove = async () => {
    if (!selectedIds.size) return;
    setBulkActing(true);
    try {
      await Promise.all(
        [...selectedIds].map(async (postId) => {
          const updates: Partial<import("../../lib/api").Post> = {};
          const copy = editCopy[postId];
          if (copy !== undefined) updates.copy = copy;
          const tags = editHashtags[postId];
          if (tags !== undefined) updates.hashtags = tags;
          if (Object.keys(updates).length > 0)
            await api.posts.update(postId, updates);
          await api.posts.approve(postId);
        }),
      );
      setPosts((prev) => prev.filter((p) => !selectedIds.has(p.postId)));
      clearSelection();
      setSelection(null);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Bulk approve failed");
    } finally {
      setBulkActing(false);
    }
  };

  const handleBulkReject = async () => {
    if (!selectedIds.size) return;
    setBulkActing(true);
    try {
      await Promise.all(
        [...selectedIds].map((postId) => api.posts.reject(postId)),
      );
      setPosts((prev) => prev.filter((p) => !selectedIds.has(p.postId)));
      clearSelection();
      setSelection(null);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Bulk reject failed");
    } finally {
      setBulkActing(false);
    }
  };

  const handleBulkSchedule = async (isoDate: string) => {
    if (!selectedIds.size || !isoDate) return;
    setBulkActing(true);
    const ts = new Date(isoDate).getTime();
    try {
      await Promise.all(
        [...selectedIds].map((postId) => api.posts.schedule(postId, ts)),
      );
      setPosts((prev) => prev.filter((p) => !selectedIds.has(p.postId)));
      clearSelection();
      setSelection(null);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Bulk schedule failed");
    } finally {
      setBulkActing(false);
    }
  };

  // ── Regenerate post copy ──────────────────────────────────────────────────────
  // Async handler: await the API until Gemini finishes and the server returns the updated post.
  const handleRegeneratePost = async (post: Post) => {
    if (post.groupId) {
      alert(
        "Regenerate copy is not available for thread tweets yet. Edit each tweet manually or reject the thread.",
      );
      return;
    }
    setRegeneratingPostId(post.postId);
    try {
      const { post: updated } = await api.posts.regenerateCopy(post.postId);
      setPosts((prev) =>
        prev.map((p) => (p.postId === updated.postId ? updated : p)),
      );
      if (
        selection?.kind === "standalone" &&
        selection.post.postId === updated.postId
      ) {
        setSelection({ kind: "standalone", post: updated });
      }
      setEditCopy((prev) => {
        const next = { ...prev };
        delete next[updated.postId];
        return next;
      });
      setEditHashtags((prev) => {
        const next = { ...prev };
        delete next[updated.postId];
        return next;
      });
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to regenerate post");
    } finally {
      setRegeneratingPostId(null);
    }
  };

  // ── Post Now (schedule 2 minutes from now) ────────────────────────────────────

  const handlePostNow = async (postId: string) => {
    setSaving(postId);
    try {
      // Try direct API publishing first
      const result = await api.posts.publish(postId);
      setPosts((prev) => prev.filter((p) => p.postId !== postId));
      if (
        selection?.kind === "standalone" &&
        selection.post.postId === postId
      ) {
        setSelection(null);
      }
      if (result.url) {
        // Brief notification — the post was published to the platform
        console.log(`Published: ${result.url}`);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // If publish fails because no account connected, fall back to schedule
      if (msg.includes("No ") && msg.includes("account connected")) {
        try {
          await api.posts.schedule(postId, Date.now() + 2 * 60 * 1000);
          setPosts((prev) => prev.filter((p) => p.postId !== postId));
          if (
            selection?.kind === "standalone" &&
            selection.post.postId === postId
          ) {
            setSelection(null);
          }
        } catch (e2) {
          alert(e2 instanceof Error ? e2.message : "Failed to schedule post");
        }
      } else {
        alert(msg);
      }
    } finally {
      setSaving(null);
    }
  };

  const handlePostNowGroup = async (group: PostGroup) => {
    setSaving(group.groupId);
    try {
      await api.posts.publishGroup(group.groupId);
      const postIds = new Set(group.posts.map((p) => p.postId));
      setPosts((prev) => prev.filter((p) => !postIds.has(p.postId)));
      setSelection(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("No ") && msg.includes("account connected")) {
        try {
          await api.posts.scheduleGroup(
            group.groupId,
            Date.now() + 2 * 60 * 1000,
          );
          const postIds = new Set(group.posts.map((p) => p.postId));
          setPosts((prev) => prev.filter((p) => !postIds.has(p.postId)));
          setSelection(null);
        } catch (e2) {
          alert(e2 instanceof Error ? e2.message : "Failed to schedule thread");
        }
      } else {
        alert(msg);
      }
    } finally {
      setSaving(null);
    }
  };

  // ── Schedule approved post ────────────────────────────────────────────────────

  const handleScheduleApproved = async (postId: string) => {
    const iso = scheduleDate[postId];
    if (!iso) return;
    setSaving(postId);
    try {
      await api.posts.schedule(postId, new Date(iso).getTime());
      setPosts((prev) => prev.filter((p) => p.postId !== postId));
      if (
        selection?.kind === "standalone" &&
        selection.post.postId === postId
      ) {
        setSelection(null);
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to schedule post");
    } finally {
      setSaving(null);
    }
  };

  const handleScheduleApprovedGroup = async (group: PostGroup) => {
    const iso = scheduleDate[group.groupId];
    if (!iso) return;
    setSaving(group.groupId);
    try {
      await api.posts.scheduleGroup(group.groupId, new Date(iso).getTime());
      const postIds = new Set(group.posts.map((p) => p.postId));
      setPosts((prev) => prev.filter((p) => !postIds.has(p.postId)));
      setSelection(null);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to schedule thread");
    } finally {
      setSaving(null);
    }
  };

  // ── Hashtag pill handlers ──────────────────────────────────────────────────────

  const handleRemoveHashtag = (postId: string, index: number) => {
    setEditHashtags((prev) => {
      const tags = [...(prev[postId] ?? [])];
      tags.splice(index, 1);
      return { ...prev, [postId]: tags };
    });
  };

  const handleAddHashtag = (postId: string, raw: string) => {
    const tag = raw.replace(/^#/, "").trim();
    if (!tag) return;
    setEditHashtags((prev) => {
      const existing = prev[postId] ?? [];
      if (existing.includes(tag)) return prev;
      return { ...prev, [postId]: [...existing, tag] };
    });
    setNewTagInput("");
    setAddingTagFor(null);
  };

  // ── Action card helpers ────────────────────────────────────────────────────────

  const toggleRejectNote = (key: string) => {
    setShowRejectNote((prev) => ({ ...prev, [key]: !prev[key] }));
    setShowSchedulePicker((prev) => ({ ...prev, [key]: false }));
  };

  const toggleSchedulePicker = (key: string) => {
    setShowSchedulePicker((prev) => ({ ...prev, [key]: !prev[key] }));
    setShowRejectNote((prev) => ({ ...prev, [key]: false }));
  };

  // ── Char count progress bar ────────────────────────────────────────────────────

  const charCountBar = (postId: string, plat: string, original: string) => {
    const text = editCopy[postId] ?? original;
    const limit = PLATFORM_CHARS[plat] ?? 500;
    const ratio = Math.min(text.length / limit, 1);
    const over = text.length > limit;
    const nearLimit = !over && ratio >= 0.85;
    const barColor = over
      ? "bg-red-500"
      : nearLimit
        ? "bg-amber-400"
        : "bg-brand-500";
    const labelColor = over
      ? "text-red-500 font-semibold"
      : nearLimit
        ? "text-amber-600"
        : "text-gray-400";
    return (
      <div className="space-y-1">
        <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${barColor}`}
            style={{ width: `${ratio * 100}%` }}
          />
        </div>
        <p className={`text-xs text-right ${labelColor}`}>
          {text.length} / {limit}
        </p>
      </div>
    );
  };

  // ── Derived ────────────────────────────────────────────────────────────────────

  const previewBrand = selectedPost
    ? brandCache.current[selectedPost.brandId]
    : undefined;

  // Controls are read-only for any non-editable status.
  // needs_revision stays editable so the creator can fix and re-approve.
  const isReadOnly = selectedPost
    ? selectedPost.status !== "pending_review" &&
      selectedPost.status !== "needs_revision"
    : false;

  // ── Render ─────────────────────────────────────────────────────────────────────

  return (
    // Height is explicitly calculated: 100vh minus AppLayout's py-8 (2rem × 2 = 4rem)
    // This gives the split panels a definite height so they can scroll independently
    // without triggering the page-level scroll in main.
    <div className="flex flex-col overflow-clip min-h-0 lg:h-[calc(100vh-4rem)]">
      {/* ── Generation banners ── */}
      {activeJobs.length > 0 && (
        <div className="space-y-2 mb-3 flex-shrink-0">
          {activeJobs.map((job) => {
            const jobStatus = jobStatuses[job.jobId];
            const isDone = jobStatus === "completed" || jobStatus === "failed";
            if (!jobStatus || isDone) return null;
            return (
              <div
                key={job.jobId}
                className="flex items-center gap-3 bg-brand-50 border border-brand-100 rounded-xl px-4 py-3"
              >
                <span className="w-3 h-3 rounded-full bg-brand-400 animate-pulse flex-shrink-0" />
                <p className="text-sm text-brand-800 flex-1">
                  {jobStatus === "queued" ? (
                    <>
                      AI is queued to generate posts for{" "}
                      <strong>{job.campaignName}</strong>…
                    </>
                  ) : (
                    <>
                      AI is writing posts for{" "}
                      <strong>{job.campaignName}</strong>…
                    </>
                  )}
                </p>
                <button
                  onClick={() => dismissJob(job.jobId)}
                  aria-label="Dismiss"
                  className="text-brand-400 hover:text-brand-700 flex-shrink-0"
                >
                  <X size={16} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Header ── */}
      <div className="flex-shrink-0 mb-5">
        <h1 className="text-2xl font-bold text-gray-900 mb-5">Review Queue</h1>

        {/* Brand / Campaign scope filters */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-1.5">
            <label
              htmlFor="brand-filter"
              className="text-xs font-medium text-gray-500"
            >
              Brand:
            </label>
            <select
              id="brand-filter"
              value={filterBrandId}
              onChange={(e) => {
                setFilterBrandId(e.target.value);
                setFilterCampaignId(""); // reset campaign when brand changes
              }}
              className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-300"
            >
              <option value="">All brands</option>
              {brands.map((b) => (
                <option key={b.brandId} value={b.brandId}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1.5">
            <label
              htmlFor="campaign-filter"
              className="text-xs font-medium text-gray-500"
            >
              Campaign:
            </label>
            <select
              id="campaign-filter"
              value={filterCampaignId}
              onChange={(e) => setFilterCampaignId(e.target.value)}
              disabled={!filterBrandId}
              className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">
                {filterBrandId ? "All campaigns" : "Select a brand first"}
              </option>
              {campaignOptions.map((c) => (
                <option key={c.campaignId} value={c.campaignId}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {(filterBrandId || filterCampaignId) && (
            <button
              onClick={() => {
                setFilterBrandId("");
                setFilterCampaignId("");
              }}
              className="text-xs text-gray-400 hover:text-gray-600 underline"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Platform tabs (primary filter) */}
        <div className="flex gap-2 flex-wrap mb-3">
          <button
            onClick={() => setPlatform("")}
            className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              platform === ""
                ? "bg-brand-500 text-white"
                : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            All
          </button>
          {ALL_PLATFORMS.map((p) => {
            const meta = PLATFORM_META[p];
            const Icon = meta.icon;
            const isActive = platform === p;
            return (
              <button
                key={p}
                onClick={() => setPlatform(p)}
                aria-pressed={isActive}
                className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-white border-2 shadow-sm"
                    : "bg-white border border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
                style={
                  isActive
                    ? { borderColor: meta.color, color: meta.color }
                    : undefined
                }
              >
                <Icon size={14} />
                {meta.short}
              </button>
            );
          })}
        </div>

        {/* Status chips (sub-filter) */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-400 font-medium">Status:</span>
          {STATUS_FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setStatus(opt.value)}
              aria-pressed={status === opt.value}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                status === opt.value
                  ? "bg-gray-800 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Bulk action bar ── */}
      {selectedIds.size > 0 && (
        <BulkActionBar
          count={selectedIds.size}
          onApprove={handleBulkApprove}
          onReject={handleBulkReject}
          onSchedule={handleBulkSchedule}
          onClear={clearSelection}
          acting={bulkActing}
        />
      )}

      {/* ── Split layout ── */}
      <div className="flex flex-col lg:flex-row gap-5 flex-1 min-h-0">
        {/* ── Left panel: post list ── */}
        <div className="lg:w-[42%] flex-shrink-0 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto space-y-3 px-0.5 py-px">
            {loading && <p className="text-gray-400 text-sm py-6">Loading…</p>}

            {/* Skeleton cards while AI is generating */}
            {!loading &&
              activeJobs.some(
                (j) =>
                  jobStatuses[j.jobId] === "queued" ||
                  jobStatuses[j.jobId] === "processing",
              ) &&
              listItems.length === 0 && (
                <>
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />
                </>
              )}

            {!loading && listItems.length === 0 && (
              <div className="card p-10 text-center">
                <p className="text-gray-400 text-sm">
                  {status === "pending_review"
                    ? "No posts pending review. Generate a batch from a campaign."
                    : "No posts found."}
                </p>
              </div>
            )}

            {listItems.map((item) => {
              // ── Standalone post card ──
              if (item.kind === "standalone") {
                const { post } = item;
                const isSelected =
                  selection?.kind === "standalone" &&
                  selection.post.postId === post.postId;
                const brandName = brandNameMap.current[post.brandId];
                const hashtags = post.hashtags.slice(0, 3);

                const engScore = computeEngagementScore(
                  post.copy,
                  post.platform,
                  post.hashtags,
                );

                return (
                  <div
                    key={post.postId}
                    className={`card overflow-hidden transition-all flex ${
                      isSelected
                        ? "ring-2 ring-brand-500 shadow-md"
                        : "hover:shadow-sm"
                    }`}
                  >
                    {/* Checkbox column */}
                    <div
                      className="flex-shrink-0 flex items-start justify-center pt-4 pl-3 pr-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelectId(post.postId);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(post.postId)}
                        onChange={() => toggleSelectId(post.postId)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-gray-300 text-brand-500 cursor-pointer"
                        aria-label={`Select post`}
                      />
                    </div>

                    <button
                      onClick={() => handleSelectStandalone(post)}
                      aria-pressed={isSelected}
                      className="flex-1 text-left min-w-0"
                    >
                      <div className="px-4 pt-4 pb-4 space-y-2">
                        {/* Meta row */}
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <PlatformIndicator
                              platform={post.platform}
                              size={13}
                            />
                            {post.imageR2Key && (
                              <ImageIcon
                                size={13}
                                className="text-gray-400 flex-shrink-0"
                              />
                            )}
                          </div>
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            {formatRelative(post.createdAt)}
                          </span>
                        </div>

                        {/* Brand name */}
                        {brandName && (
                          <p className="text-xs font-semibold text-gray-500 truncate">
                            {brandName}
                          </p>
                        )}

                        {/* Copy preview */}
                        <p className="text-sm text-gray-800 line-clamp-2 leading-snug">
                          {post.copy}
                        </p>

                        {/* Hashtags + engagement score */}
                        <div className="flex items-center justify-between gap-2">
                          {hashtags.length > 0 ? (
                            <p className="text-xs text-gray-400 truncate flex-1">
                              {hashtags.map((h) => `#${h}`).join(" · ")}
                            </p>
                          ) : (
                            <span />
                          )}
                          <EngagementBadge score={engScore} />
                        </div>
                      </div>
                    </button>
                  </div>
                );
              }

              // ── Thread group card ──
              const { group } = item;
              const isGroupSelected =
                selection?.kind === "thread" &&
                selection.group.groupId === group.groupId;
              const firstPost = group.posts[0];
              const brandName = firstPost
                ? brandNameMap.current[firstPost.brandId]
                : undefined;

              return (
                <div
                  key={group.groupId}
                  className={`card overflow-hidden transition-all ${
                    isGroupSelected ? "ring-2 ring-brand-500 shadow-md" : ""
                  }`}
                >
                  {/* Thread header — clicking selects tweet 1 */}
                  <button
                    onClick={() => handleSelectGroup(group)}
                    aria-pressed={isGroupSelected}
                    className="w-full text-left px-5 pt-4 pb-3 hover:bg-gray-50 transition-colors"
                  >
                    {/* Meta row */}
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <PlatformIndicator platform="twitter" size={13} />
                        <span className="text-xs text-gray-400">
                          Thread · {group.posts.length} tweets
                        </span>
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {firstPost ? formatRelative(firstPost.createdAt) : ""}
                      </span>
                    </div>

                    {/* Brand name */}
                    {brandName && (
                      <p className="text-xs font-semibold text-gray-500 mb-1.5 truncate">
                        {brandName}
                      </p>
                    )}

                    {/* First tweet preview */}
                    <p className="text-sm text-gray-800 truncate leading-snug">
                      {firstPost?.copy}
                    </p>
                  </button>

                  {/* Thread tweet sub-rows */}
                  <div className="border-t border-gray-100">
                    {group.posts.map((post, idx) => {
                      const isTweetSelected =
                        selection?.kind === "thread" &&
                        selection.group.groupId === group.groupId &&
                        selection.activeIndex === idx;
                      const isLast = idx === group.posts.length - 1;

                      return (
                        <button
                          key={post.postId}
                          onClick={() =>
                            setSelection({
                              kind: "thread",
                              group,
                              activeIndex: idx,
                            })
                          }
                          className={`w-full text-left flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${
                            !isLast ? "border-b border-gray-50" : ""
                          } ${isTweetSelected ? "bg-brand-50" : "hover:bg-gray-50"}`}
                        >
                          <span className="text-xs text-gray-400 font-mono flex-shrink-0 w-7">
                            {isLast ? "└" : "├"} {idx + 1}/{group.posts.length}
                          </span>
                          <span className="text-gray-700 truncate text-xs">
                            {post.copy}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Right panel: preview + edit ── */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          {!selectedPost ? (
            <div className="flex-1 card flex flex-col items-center justify-center text-center p-10">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </div>
              <p className="text-gray-500 font-medium">
                Select a post to preview
              </p>
              <p className="text-gray-400 text-sm mt-1">
                Click any post or thread on the left to review it here
              </p>
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4">
              {/* ── Thread warning banner ── */}
              {selection?.kind === "thread" && (
                <div className="flex items-center justify-between border-l-4 border-amber-400 bg-amber-50 rounded-xl px-4 py-2.5">
                  <span className="text-sm font-medium text-amber-800 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                    Post {selection.activeIndex + 1} of{" "}
                    {selection.group.posts.length} — review companion post
                    before approving
                  </span>
                  <div className="flex items-center gap-0.5 ml-3 flex-shrink-0">
                    <button
                      onClick={() => handleThreadNav(-1)}
                      disabled={selection.activeIndex === 0}
                      aria-label="Previous tweet"
                      className="p-1.5 rounded text-amber-700 hover:bg-amber-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => handleThreadNav(1)}
                      disabled={
                        selection.activeIndex ===
                        selection.group.posts.length - 1
                      }
                      aria-label="Next tweet"
                      className="p-1.5 rounded text-amber-700 hover:bg-amber-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* ── Platform preview ── */}
              <div className="card p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
                  {PLATFORM_META[selectedPost.platform]?.label ??
                    selectedPost.platform}{" "}
                  Preview
                </p>
                <PostPreview
                  post={{
                    ...selectedPost,
                    copy: editCopy[selectedPost.postId] ?? selectedPost.copy,
                  }}
                  imageUrl={previewImageUrl}
                  brandName={previewBrand?.name}
                  brandColors={previewBrand?.colors}
                />
              </div>

              {/* ── Copy editor ── */}
              <div className="card p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {isReadOnly ? "Post Copy" : "Edit Copy"}
                  </p>
                  <div className="flex items-center gap-2">
                    {selectedPost.status === "pending_review" && (
                      <button
                        onClick={() => void handleRegeneratePost(selectedPost)}
                        disabled={
                          regeneratingPostId === selectedPost.postId ||
                          !!selectedPost.groupId
                        }
                        aria-label="Regenerate this post's copy with AI"
                        title={
                          selectedPost.groupId
                            ? "Not available for thread tweets"
                            : "Replace copy with a fresh AI draft (waits until ready)"
                        }
                        className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-800 border border-brand-200 hover:border-brand-400 px-2 py-1 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {regeneratingPostId === selectedPost.postId ? (
                          <span className="flex items-center gap-1.5">
                            <span className="w-3 h-3 border border-brand-400 border-t-brand-700 rounded-full animate-spin inline-block" />
                            Regenerating…
                          </span>
                        ) : (
                          <>↺ Regenerate</>
                        )}
                      </button>
                    )}
                    {isReadOnly && (
                      <span className="text-xs text-gray-400 italic">
                        Read-only — click Edit below to modify
                      </span>
                    )}
                  </div>
                </div>

                {/* Refinement quick-action buttons — Rephrase / Expand / Shorten */}
                {selectedPost.status === "pending_review" &&
                  !selectedPost.groupId && (
                    <div className="flex gap-1.5 flex-wrap">
                      {[
                        {
                          label: "Rephrase",
                          hint: "Rephrase this post to say the same thing in a fresh, different way",
                        },
                        {
                          label: "Expand",
                          hint: "Expand this post to be more detailed and elaborate while staying on-brand",
                        },
                        {
                          label: "Shorten",
                          hint: "Make this post shorter and more punchy while keeping the key message",
                        },
                      ].map(({ label, hint }) => (
                        <button
                          key={label}
                          onClick={async () => {
                            setRegeneratingPostId(selectedPost.postId);
                            try {
                              const { post: updated } =
                                await api.posts.regenerateCopy(
                                  selectedPost.postId,
                                  { promptHint: hint },
                                );
                              setPosts((prev) =>
                                prev.map((p) =>
                                  p.postId === updated.postId ? updated : p,
                                ),
                              );
                              setSelection({
                                kind: "standalone",
                                post: updated,
                              });
                              setEditCopy((prev) => {
                                const next = { ...prev };
                                delete next[updated.postId];
                                return next;
                              });
                              setEditHashtags((prev) => {
                                const next = { ...prev };
                                delete next[updated.postId];
                                return next;
                              });
                            } catch (e) {
                              alert(
                                e instanceof Error
                                  ? e.message
                                  : "Refinement failed",
                              );
                            } finally {
                              setRegeneratingPostId(null);
                            }
                          }}
                          disabled={regeneratingPostId === selectedPost.postId}
                          title={hint}
                          className="text-xs text-gray-600 border border-gray-200 hover:border-brand-300 hover:text-brand-700 hover:bg-brand-50 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {regeneratingPostId === selectedPost.postId ? (
                            <span className="w-3 h-3 border border-gray-400 border-t-gray-700 rounded-full animate-spin inline-block" />
                          ) : (
                            label
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                <textarea
                  className={`input w-full min-h-[120px] resize-none text-sm ${
                    isReadOnly
                      ? "bg-gray-50 text-gray-500 cursor-not-allowed"
                      : ""
                  }`}
                  value={editCopy[selectedPost.postId] ?? selectedPost.copy}
                  disabled={isReadOnly}
                  onChange={(e) =>
                    setEditCopy((prev) => ({
                      ...prev,
                      [selectedPost.postId]: e.target.value,
                    }))
                  }
                />

                {/* Character progress bar — directly below textarea */}
                {charCountBar(
                  selectedPost.postId,
                  selectedPost.platform,
                  selectedPost.copy,
                )}

                {/* Hashtag pills — read-only or editable */}
                <div className="flex flex-wrap gap-1.5 items-center">
                  {(
                    editHashtags[selectedPost.postId] ?? selectedPost.hashtags
                  ).map((h, i) => (
                    <span
                      key={`${h}-${i}`}
                      className="inline-flex items-center gap-1 text-xs bg-brand-50 text-brand-700 pl-2 pr-1 py-0.5 rounded-full"
                    >
                      #{h}
                      {!isReadOnly && (
                        <button
                          onClick={() =>
                            handleRemoveHashtag(selectedPost.postId, i)
                          }
                          aria-label={`Remove #${h}`}
                          className="text-brand-400 hover:text-brand-700 rounded-full focus:outline-none"
                        >
                          <X size={10} />
                        </button>
                      )}
                    </span>
                  ))}

                  {!isReadOnly &&
                    (addingTagFor === selectedPost.postId ? (
                      <input
                        autoFocus
                        className="text-xs border border-brand-300 rounded-full px-2 py-0.5 w-24 focus:outline-none focus:ring-1 focus:ring-brand-400"
                        placeholder="#tag"
                        value={newTagInput}
                        onChange={(e) => setNewTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleAddHashtag(selectedPost.postId, newTagInput);
                          }
                          if (e.key === "Escape") {
                            setAddingTagFor(null);
                            setNewTagInput("");
                          }
                        }}
                        onBlur={() => {
                          if (newTagInput.trim())
                            handleAddHashtag(selectedPost.postId, newTagInput);
                          else {
                            setAddingTagFor(null);
                            setNewTagInput("");
                          }
                        }}
                      />
                    ) : (
                      <button
                        onClick={() => {
                          setAddingTagFor(selectedPost.postId);
                          setNewTagInput("");
                        }}
                        className="inline-flex items-center gap-0.5 text-xs text-gray-400 hover:text-brand-600 transition-colors"
                      >
                        <Plus size={11} />
                        add tag
                      </button>
                    ))}
                </div>
              </div>

              {/* ── Image picker ── */}
              <div className="card p-4 space-y-3">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Post Image
                  </p>
                  {!isReadOnly && (
                    <label
                      aria-label="Upload image"
                      className={`text-xs text-brand-600 hover:underline cursor-pointer ${
                        savingImage === selectedPost.postId
                          ? "opacity-50 pointer-events-none"
                          : ""
                      }`}
                    >
                      + Upload image
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        style={{
                          position: "absolute",
                          left: "-9999px",
                          top: "0px",
                        }}
                        ref={(el) => {
                          uploadInputRefs.current[selectedPost.postId] = el;
                        }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleUploadImage(selectedPost, file);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  )}
                </div>

                {(() => {
                  const assets = campaignAssets[selectedPost.campaignId] ?? [];
                  const isSavingImage = savingImage === selectedPost.postId;

                  if (assets.length === 0) {
                    return (
                      <p className="text-xs text-gray-400 italic">
                        {isReadOnly
                          ? "No image attached."
                          : "No campaign images yet. Upload one or import images from the Campaign Editor."}
                      </p>
                    );
                  }

                  return (
                    <div className="flex gap-2 flex-wrap">
                      {assets.map((r2Key) => {
                        const blobUrl = assetBlobUrls[r2Key];
                        const isSelected = selectedPost.imageR2Key === r2Key;
                        return (
                          <button
                            key={r2Key}
                            onClick={() => {
                              if (!isReadOnly)
                                handleSelectImage(selectedPost, r2Key);
                            }}
                            disabled={isSavingImage || isReadOnly}
                            aria-label={
                              isSelected ? "Remove image" : "Use this image"
                            }
                            aria-pressed={isSelected}
                            className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                              isSelected
                                ? "border-brand-500 shadow-md"
                                : isReadOnly
                                  ? "border-gray-200"
                                  : "border-gray-200 hover:border-brand-300"
                            } ${isSavingImage ? "opacity-50 cursor-wait" : ""} ${isReadOnly ? "cursor-default" : ""}`}
                          >
                            {blobUrl ? (
                              <img
                                src={blobUrl}
                                alt="Campaign asset"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                <span className="text-gray-400 text-xs">…</span>
                              </div>
                            )}
                            {isSelected && (
                              <div className="absolute inset-0 bg-brand-500/20 flex items-center justify-center">
                                <span className="bg-brand-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
                                  <Check size={11} />
                                </span>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}

                {savingImage === selectedPost.postId && (
                  <p className="text-xs text-brand-600">Saving image…</p>
                )}

                {!isReadOnly &&
                  selectedPost.imageR2Key === null &&
                  (campaignAssets[selectedPost.campaignId] ?? []).length ===
                    0 && (
                    <p className="text-xs text-gray-400 italic">
                      No campaign images yet. Import images from the Campaign
                      Editor to attach them to posts.
                    </p>
                  )}
              </div>

              {/* ── OG card ── */}
              {selectedPost.openGraph && (
                <div className="card p-4">
                  <div className="border border-gray-200 rounded-lg p-3 bg-white">
                    <p className="text-xs font-semibold text-gray-700 truncate">
                      {selectedPost.openGraph.title}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                      {selectedPost.openGraph.description}
                    </p>
                    <p className="text-xs text-brand-600 mt-0.5 truncate">
                      {selectedPost.openGraph.url}
                    </p>
                  </div>
                </div>
              )}

              {/* ── Actions: pending_review ── */}
              {selectedPost.status === "pending_review" && (
                <div className="card p-4 space-y-3">
                  {selection?.kind === "thread" && (
                    <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                      This action applies to all {selection.group.posts.length}{" "}
                      tweets in the thread.
                    </p>
                  )}

                  {(() => {
                    const isThread = selection?.kind === "thread";
                    const actionKey = isThread
                      ? selection!.group.groupId
                      : selectedPost.postId;
                    const isSaving = saving === actionKey;
                    const isRejectOpen = showRejectNote[actionKey] ?? false;
                    const isScheduleOpen =
                      showSchedulePicker[actionKey] ?? false;

                    const handleApproveAction = () =>
                      isThread
                        ? handleApproveGroup(selection!.group)
                        : handleApprove(selectedPost.postId);

                    const handleRejectAction = () =>
                      isThread
                        ? handleRejectGroup(selection!.group)
                        : handleReject(selectedPost.postId);

                    const handleScheduleAction = () =>
                      isThread
                        ? handleScheduleGroup(selection!.group)
                        : handleSchedule(selectedPost.postId);

                    const isRevisionOpen = showRevisionNote[actionKey] ?? false;
                    const approveLabel = isThread
                      ? "Approve Thread"
                      : "Approve";

                    return (
                      <>
                        <button
                          onClick={handleApproveAction}
                          disabled={isSaving}
                          className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
                        >
                          {isSaving ? (
                            "…"
                          ) : (
                            <>
                              <Check size={15} />
                              {approveLabel}
                            </>
                          )}
                        </button>

                        <div className="grid grid-cols-3 gap-2">
                          <button
                            onClick={() => toggleSchedulePicker(actionKey)}
                            disabled={isSaving}
                            className={`btn-secondary flex items-center justify-center gap-1 text-xs ${
                              isScheduleOpen
                                ? "ring-2 ring-brand-400 border-brand-400"
                                : ""
                            }`}
                          >
                            <CalendarClock size={13} />
                            Schedule
                          </button>
                          {/* Send for revision — softer than reject; marks post as needs_revision */}
                          {!isThread && (
                            <button
                              onClick={() => toggleRevisionNote(actionKey)}
                              disabled={isSaving}
                              className={`flex items-center justify-center gap-1 text-xs rounded-lg px-2 py-2 font-medium transition-colors border ${
                                isRevisionOpen
                                  ? "bg-amber-50 text-amber-700 border-amber-300"
                                  : "text-amber-600 border-transparent hover:bg-amber-50 hover:border-amber-200"
                              }`}
                            >
                              ↩ Revise
                            </button>
                          )}
                          <button
                            onClick={() => toggleRejectNote(actionKey)}
                            disabled={isSaving}
                            className={`flex items-center justify-center gap-1 text-xs rounded-lg px-2 py-2 font-medium transition-colors border ${
                              isThread ? "col-span-2" : ""
                            } ${
                              isRejectOpen
                                ? "bg-red-50 text-red-700 border-red-300"
                                : "text-red-600 border-transparent hover:bg-red-50 hover:border-red-200"
                            }`}
                          >
                            <X size={13} />
                            {isThread ? "Reject Thread" : "Reject"}
                          </button>
                        </div>

                        {/* Send for revision panel */}
                        {isRevisionOpen && !isThread && (
                          <div className="space-y-2 pt-1 border border-amber-200 rounded-lg p-3 bg-amber-50">
                            <p className="text-xs text-amber-800 font-medium">
                              Send for Revision
                            </p>
                            <p className="text-xs text-amber-700">
                              The post stays in the queue marked as "Needs
                              Revision". Add a note explaining what to change.
                            </p>
                            <input
                              className="input text-sm w-full"
                              placeholder="What needs to change? (optional)"
                              value={revisionNote[actionKey] ?? ""}
                              onChange={(e) =>
                                setRevisionNote((prev) => ({
                                  ...prev,
                                  [actionKey]: e.target.value,
                                }))
                              }
                            />
                            <button
                              onClick={() =>
                                handleSendForRevision(selectedPost.postId)
                              }
                              disabled={isSaving}
                              className="w-full text-sm flex items-center justify-center gap-2 rounded-lg px-4 py-2 font-medium bg-amber-500 text-white hover:bg-amber-600 transition-colors disabled:opacity-50"
                            >
                              {isSaving ? "…" : "↩ Mark for Revision"}
                            </button>
                          </div>
                        )}

                        {isScheduleOpen && (
                          <div className="space-y-2 pt-1">
                            <input
                              type="datetime-local"
                              className="input text-sm w-full"
                              min={new Date().toISOString().slice(0, 16)}
                              value={scheduleDate[actionKey] ?? ""}
                              onChange={(e) =>
                                setScheduleDate((prev) => ({
                                  ...prev,
                                  [actionKey]: e.target.value,
                                }))
                              }
                            />
                            <button
                              onClick={handleScheduleAction}
                              disabled={isSaving || !scheduleDate[actionKey]}
                              className="btn-primary w-full text-sm flex items-center justify-center gap-2"
                            >
                              {isSaving ? (
                                "…"
                              ) : (
                                <>
                                  <CalendarClock size={14} />
                                  Confirm Schedule
                                </>
                              )}
                            </button>
                          </div>
                        )}

                        {isRejectOpen && (
                          <div className="space-y-2 pt-1">
                            <input
                              className="input text-sm w-full"
                              placeholder="Rejection note (optional — feeds back into future generation)"
                              value={rejectNote[actionKey] ?? ""}
                              onChange={(e) =>
                                setRejectNote((prev) => ({
                                  ...prev,
                                  [actionKey]: e.target.value,
                                }))
                              }
                            />
                            <button
                              onClick={handleRejectAction}
                              disabled={isSaving}
                              className="btn-danger w-full text-sm flex items-center justify-center gap-2"
                            >
                              {isSaving ? (
                                "…"
                              ) : (
                                <>
                                  <X size={14} />
                                  {isThread
                                    ? "Confirm Reject Thread"
                                    : "Confirm Reject"}
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* ── Actions: needs_revision ── */}
              {selectedPost.status === "needs_revision" && (
                <div className="card p-4 space-y-3 border-l-4 border-amber-400">
                  <div className="flex items-start gap-2">
                    <span className="text-amber-500 text-base flex-shrink-0">
                      ↩
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-amber-800">
                        Needs Revision
                      </p>
                      {selectedPost.rejectionNote && (
                        <p className="text-xs text-amber-700 mt-0.5 italic">
                          "{selectedPost.rejectionNote}"
                        </p>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Edit the copy above, then approve or schedule when ready.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => void handleApprove(selectedPost.postId)}
                      disabled={saving === selectedPost.postId}
                      className="btn-primary flex-1 flex items-center justify-center gap-2 text-sm"
                    >
                      <Check size={14} />
                      Approve
                    </button>
                    <button
                      onClick={() =>
                        void handleRevertToPending(selectedPost.postId)
                      }
                      disabled={saving === selectedPost.postId}
                      className="btn-secondary flex-1 text-sm"
                    >
                      Back to Pending
                    </button>
                  </div>
                </div>
              )}

              {/* ── Actions: approved ── */}
              {selectedPost.status === "approved" && (
                <div className="card p-4 space-y-3">
                  {selection?.kind === "thread" && (
                    <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                      This action applies to all {selection.group.posts.length}{" "}
                      tweets in the thread.
                    </p>
                  )}

                  {(() => {
                    const isThread = selection?.kind === "thread";
                    const actionKey = isThread
                      ? selection!.group.groupId
                      : selectedPost.postId;
                    const isSaving = saving === actionKey;
                    const isScheduleOpen =
                      showSchedulePicker[actionKey] ?? false;

                    const handlePostNowAction = () =>
                      isThread
                        ? handlePostNowGroup(selection!.group)
                        : handlePostNow(selectedPost.postId);

                    const handleEditAction = () =>
                      isThread
                        ? handleRevertGroupToPending(selection!.group)
                        : handleRevertToPending(selectedPost.postId);

                    const handleScheduleConfirm = () =>
                      isThread
                        ? handleScheduleApprovedGroup(selection!.group)
                        : handleScheduleApproved(selectedPost.postId);

                    return (
                      <>
                        {/* Post Now — primary action */}
                        <button
                          onClick={handlePostNowAction}
                          disabled={isSaving}
                          className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
                        >
                          {isSaving ? (
                            "…"
                          ) : (
                            <>
                              <SendHorizonal size={15} />
                              {isThread ? "Publish Thread" : "Publish Now"}
                            </>
                          )}
                        </button>

                        {/* Secondary row: Schedule + Edit */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => toggleSchedulePicker(actionKey)}
                            disabled={isSaving}
                            className={`btn-secondary flex-1 flex items-center justify-center gap-1.5 text-sm ${
                              isScheduleOpen
                                ? "ring-2 ring-brand-400 border-brand-400"
                                : ""
                            }`}
                          >
                            <CalendarClock size={14} />
                            Schedule
                          </button>
                          <button
                            onClick={handleEditAction}
                            disabled={isSaving}
                            className="flex-1 flex items-center justify-center gap-1.5 text-sm rounded-lg px-4 py-2 font-medium transition-colors border text-gray-600 border-transparent hover:bg-gray-50 hover:border-gray-200"
                          >
                            <Pencil size={14} />
                            {isThread ? "Edit Thread" : "Edit"}
                          </button>
                        </div>

                        {/* Schedule date picker */}
                        {isScheduleOpen && (
                          <div className="space-y-2 pt-1">
                            <input
                              type="datetime-local"
                              className="input text-sm w-full"
                              min={new Date().toISOString().slice(0, 16)}
                              value={scheduleDate[actionKey] ?? ""}
                              onChange={(e) =>
                                setScheduleDate((prev) => ({
                                  ...prev,
                                  [actionKey]: e.target.value,
                                }))
                              }
                            />
                            <button
                              onClick={handleScheduleConfirm}
                              disabled={isSaving || !scheduleDate[actionKey]}
                              className="btn-primary w-full text-sm flex items-center justify-center gap-2"
                            >
                              {isSaving ? (
                                "…"
                              ) : (
                                <>
                                  <CalendarClock size={14} />
                                  Confirm Schedule
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
